import { useRef, useState, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSettings } from '@/SettingsContext';
import { useLanguage } from '@/LanguageContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';


// Fallback görseller — API boşsa bunlar gösterilir
const FALLBACK_ITEMS = [
  { id: '1', src: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80', type: 'image', aspect: 'aspect-[3/4]', order: 0 },
  { id: '2', src: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600&q=80', type: 'image', aspect: 'aspect-[4/3]', order: 1 },
  { id: '3', src: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80', type: 'image', aspect: 'aspect-[3/4]', order: 2 },
  { id: '4', src: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600&q=80', type: 'image', aspect: 'aspect-[1/1]', order: 3 },
  { id: '5', src: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&q=80', type: 'image', aspect: 'aspect-[3/4]', order: 4 },
  { id: '6', src: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80', type: 'image', aspect: 'aspect-[3/4]', order: 5 },
  { id: '7', src: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=600&q=80', type: 'image', aspect: 'aspect-[4/3]', order: 6 },
  { id: '8', src: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&q=80', type: 'image', aspect: 'aspect-[3/4]', order: 7 },
];

// Text reveal animation
const TextReveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: '100%' }}
        animate={isInView ? { y: 0 } : { y: '100%' }}
        transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};

// Tek galeri kartı — video ya da görsel
const GalleryCard = ({ item, index, isInView }) => {
  const aspect = item.aspect || 'aspect-[3/4]';
  const isVideo = item.type === 'video';

  return (
    <motion.div
      key={item.id}
      data-testid={`gallery-image-${index}`}
      className="flex-shrink-0 relative group cursor-pointer"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.6, delay: 0.3 + index * 0.05 }}
      whileHover={{ y: -10 }}
    >
      <div className={`w-56 md:w-64 ${aspect} overflow-hidden bg-black/5 dark:bg-white/5`}>
        {isVideo ? (
          <video
            src={item.src}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <motion.img
            src={item.src}
            alt={`Gallery ${index + 1}`}
            className="w-full h-full object-cover"
            initial={{ filter: 'grayscale(100%)' }}
            whileHover={{ filter: 'grayscale(0%)', scale: 1.05 }}
            transition={{ duration: 0.5 }}
          />
        )}
      </div>
      {/* Number overlay */}
      <motion.div
        className="absolute bottom-4 left-4 font-mono text-xs text-black/60 dark:text-white/40 group-hover:text-black dark:text-white/80 transition-colors"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 + index * 0.05 }}
      >
        {String(index + 1).padStart(2, '0')}
      </motion.div>
      {/* Video badge */}
      {isVideo && (
        <div className="absolute top-3 right-3 font-mono text-[9px] px-1.5 py-0.5 border border-black/30 dark:border-white/20 text-black/60 dark:text-white/40 bg-white dark:bg-black/60">
          VIDEO
        </div>
      )}
    </motion.div>
  );
};

export const GallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const apiSettings = useSettings();
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-10%']);

  useEffect(() => {
    fetch(`${API}/gallery`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setItems(data);
        } else {
          setItems(FALLBACK_ITEMS); // DB boşsa fallback göster
        }
        setLoaded(true);
      })
      .catch(() => {
        setItems(FALLBACK_ITEMS); // API erişilemezse fallback
        setLoaded(true);
      });
  }, []);

  const nextSlide = () => setCurrentIndex(prev => (prev + 1) % items.length);
  const prevSlide = () => setCurrentIndex(prev => (prev - 1 + items.length) % items.length);

  // Her kartın genişliği + gap = 240px (w-56 md:w-64 ≈ 256px + 16px gap)
  const CARD_WIDTH = 272;

  return (
    <section
      id="gallery"
      ref={ref}
      data-testid="gallery-section"
      className="relative py-32 md:py-48 overflow-hidden bg-[#030303]"
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto mb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <motion.span
              className="inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-black/50 dark:text-white/30 border border-black/20 dark:border-white/10 px-4 py-2 mb-8"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            >
              {t('gallery', 'subtitle', '© GÖRSEL ARŞİV')}
            </motion.span>
            <TextReveal>
              <h2 className="text-3xl md:text-4xl font-display font-bold whitespace-pre-line text-white dark:text-white">
                {t('gallery', 'title', 'We are KNOXOPIA srg')}
              </h2>
            </TextReveal>
          </div>

          {/* Navigation */}
          <motion.div
            className="flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          >
            <span className="font-mono text-xs text-black/40 dark:text-white/20">
              {String(currentIndex + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
            </span>
            <button
              data-testid="gallery-prev"
              onClick={prevSlide}
              className="w-10 h-10 border border-black/30 dark:border-white/20 hover:border-black/70 dark:border-white/60 hover:bg-black/5 dark:bg-white/5 transition-all flex items-center justify-center"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="gallery-next"
              onClick={nextSlide}
              className="w-10 h-10 border border-black/30 dark:border-white/20 hover:border-black/70 dark:border-white/60 hover:bg-black/5 dark:bg-white/5 transition-all flex items-center justify-center"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>

        {/* Description */}
        <motion.p
          className="text-white/60 dark:text-white/40 font-body text-base md:text-lg max-w-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {t('gallery', 'desc', apiSettings?.gallery_desc ?? 'During your training, you will immerse yourself in the work of the entire KNOXOPIA team and become part of it, gaining practical experience.')}
        </motion.p>
      </div>

      <motion.div className="px-6 md:px-12 mb-8 overflow-hidden" style={{ x }}>
        <h3 className="text-6xl md:text-8xl lg:text-[12rem] font-display font-bold tracking-tight text-white/[0.03] whitespace-nowrap">
          {t('gallery', 'bg_text', "KNOXOPIA SRG'_")}
        </h3>
      </motion.div>

      {/* Image slider */}
      <div className="relative overflow-hidden">
        <motion.div
          className="flex gap-4 px-6 md:px-12"
          animate={{ x: -currentIndex * CARD_WIDTH }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          {items.map((item, index) => (
            <GalleryCard key={item.id} item={item} index={index} isInView={isInView} />
          ))}
        </motion.div>

        {/* Gradient kenarlar */}
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#030303] to-transparent pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#030303] to-transparent pointer-events-none" />
      </div>

      {/* Slide indicators */}
      <div className="px-6 md:px-12 mt-8 flex items-center gap-2">
        {items.map((_, index) => (
          <motion.button
            key={index}
            className="h-[2px]"
            style={{ width: index === currentIndex ? 24 : 16 }}
            animate={{ backgroundColor: index === currentIndex ? '#fff' : 'rgba(255,255,255,0.15)' }}
            onClick={() => setCurrentIndex(index)}
            whileHover={{ backgroundColor: 'rgba(255,255,255,0.4)' }}
          />
        ))}
      </div>
    </section>
  );
};

export default GallerySection;